import React, { Fragment, useEffect, useState } from 'react';
import { SupabaseClient, User } from '@supabase/supabase-js';
import { CareerMap } from '../../types';
import camelcaseKeys from 'camelcase-keys';
import { createClient } from '../../lib/supabaseSetup/client';
import { toast } from 'react-toastify';
import { PostgrestError } from '@supabase/supabase-js';
import { Transition } from '@headlessui/react';
import { Portal } from '@headlessui/react';
import {
  ChatBubbleLeftEllipsisIcon,
  ChevronUpDownIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { Popover } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { useAtom, useAtomValue } from 'jotai';
import { FaMap } from 'react-icons/fa';
import {
  jobTitleAtom,
  mapDataAtom,
  pass1LearningPathAtom,
  isStreamingMapAtom,
  pass2LearningPathAtom,
} from '../../jotaiStore';
import Link from 'next/link';
import { Loader2, Share2 } from 'lucide-react';
import { FaRegShareSquare } from 'react-icons/fa';
import { FaShareNodes } from 'react-icons/fa6';
import { convertPostgresDate } from '../../lib/date';
import LoadingOverlay from '../global/loading/LoadingOverlay';
import LoadingSpinnerSmall from '../global/loading/LoadingSpinnerSmall';
import clsx from 'clsx';
import { createMarkdownPost } from '@/app/actions/createMarkdownPost';
import { AnimatePresence, motion } from 'framer-motion';
import FeedbackModal from '../global/FeedbackModal';
import {
  UserMenuHeader,
  UserMenuMainContent,
  UserMenuFooter,
} from '../global/UserMenu';
import { useParams } from 'next/navigation';
import { Progress } from '@/components/ui/progress';
import MapGenerationProgress from './MapGenerationProgress';
import EmptyState from '../global/UserMenuEmptyState';

const fetchUsersSavedMaps = async (
  user: User
): Promise<{ data: CareerMap[] | null; error: PostgrestError | null }> => {
  const SupabaseClient = createClient();

  //fetch all maps that match the user's id
  const { data, error } = await SupabaseClient.from('maps')
    .select('id, job_title, slug, updated_at')
    .eq('user_id', user.id)
    .order('updated_at', { ascending: true });

  if (error) throw error; // React Query will catch this

  return { data: camelcaseKeys(data) as CareerMap[], error: null };
};

type RoadMapPanelProps = {
  user: User | null;
};

const RoadMapPanelHeader = ({ user }: RoadMapPanelProps) => {
  const {
    data: usersMaps,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['userMaps', user?.id],
    queryFn: () => fetchUsersSavedMaps(user!),
    enabled: !!user, // Disable automatic fetching
    staleTime: 0, // Data is considered stale immediately
  });

  const jobTitle = useAtomValue(jobTitleAtom);
  
  const isStreaming = useAtomValue(isStreamingMapAtom);
  const pass1Path = useAtomValue(pass1LearningPathAtom);
  
  const pass2Path = useAtomValue(pass2LearningPathAtom);

  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileSidebar, setShowMobileSidebar] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = React.useState(false);
  const mapSlug = useParams().mapSlug;
  // Render fetch button and results
  return (
    <Popover className="relative max-w-full">
      {showFeedbackModal && (
        <AnimatePresence>
          <FeedbackModal
            handleClose={() => {
              setShowFeedbackModal(false);
            }}
            user={user}
          />
        </AnimatePresence>
      )}
      <Popover.Button
        className="flex justify-between p-3 px-4 bg-shades-baseLight border-[1px] border-white/25 text-shades-text/90 rounded-lg cursor-pointer w-full relative focus:outline-none focus-visible:outline-none focus:ring-0 hover:brightness-105"
        onClick={() => {
          if (user) {
            refetch();
          }
          setShowUserMenu(true);
        }}
        aria-haspopup="true"
        aria-label="User Menu"
        aria-controls="userMenu"
        disabled={isLoading}
      >
        <div className="flex flex-col w-full">
          {isStreaming && !pass1Path && !jobTitle ? (
            <Loader2 className="w-8 h-8 text-colors-green animate-spin" />
          ) : (
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center justify-start h-8">
                <div className="hidden @mobile/sidebar:block rounded-lg bg-colors-green text-shades-base p-2">
                  <Share2 className="w-6 h-6 stroke-2" />
                </div>
                <div className="flex flex-col text-left relative top-[1px]">
                  <AnimatePresence mode="wait">
                    {!isStreaming && pass2Path && (
                      <motion.div
                        initial={{ opacity: 0, y: 5, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: 'auto' }}
                        exit={{ opacity: 0, y: -10, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeInOut' }}
                      >
                        <span className="relative text-xs leading-[14px] font-medium tracking-widest left-px opacity-80">
                          CAREER MAP
                        </span>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  <motion.div
                    initial={{ y: 0 }}
                    animate={{ y: isStreaming ? 0 : -5 }}
                    transition={{ duration: 0.3, ease: 'easeInOut' }}
                  >
                    <span
                      className="font-thin leading-[22px] @mobile/sidebar:text-[24px] @mobile/sidebar:leading-[26px] -mt-[1px] text-shades-text tracking-wider"
                      style={{ fontSize: isStreaming ? '24px' : '20px' }}
                    >
                      {jobTitle || ''}
                    </span>
                  </motion.div>
                </div>
              </div>
              <ChevronUpDownIcon
                className="h-7 w-7 shrink-0 relative left-2.5 -mt-3"
                aria-hidden="true"
              />
            </div>
          )}
          <MapGenerationProgress />
        </div>
      </Popover.Button>

      <Popover.Overlay as="div" className="top-0 left-0 z-50 " />
      {showUserMenu && (
        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel
            id="userMenu"
            className={`absolute ${
              isStreaming ? 'top-[115px]' : 'top-[75px]'
            } z-[100] max-w-full flex`}
          >
            <div
              // zIndex="popover"
              className="flex flex-col w-screen flex-auto overflow-hidden rounded-lg bg-shades-baseLight backdrop-blur-md text-sm leading-6 shadow-md ring-1 ring-gray-900/5 max-h-[calc(100vh-240px)] relative right-[1px] border-[1px] border-shades-text/10"
            >
              {isLoading ? (
                <LoadingState />
              ) : (
                <>
                  {error ? (
                    <ErrorState />
                  ) : (
                    <>
                      {!usersMaps?.data ? (
                        <EmptyState user={user} />
                      ) : (
                        <>
                          <UserMenuHeader user={user} />
                          <UserMenuMainContent
                            isLoading={isLoading}
                            savedMaps={usersMaps?.data || null}
                            user={user}
                            setShowUserMenu={setShowUserMenu}
                          />
                          <UserMenuFooter
                            user={user}
                            setShowFeedbackModal={setShowFeedbackModal}
                            mapSlug={mapSlug}
                            setIsLoading={() => {}}
                          />
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </Popover.Panel>
        </Transition>
      )}
    </Popover>
  );
};

const LoadingState = () => {
  return (
    <div className="flex flex-col w-screen flex-auto overflow-hidden rounded-lg bg-shades-baseLighter backdrop-blur-md text-sm leading-6 shadow-lg ring-1 ring-gray-900/5 max-h-[calc(100vh-200px)] relative right-[1px] py-8 justify-center items-center">
      <LoadingSpinnerSmall />
      <p className="mt-4 text-sm font-semibold leading-6 text-colors-green">
        Loading Maps...
      </p>
    </div>
  );
};

const ErrorState = () => {
  return (
    <div className="flex flex-col w-screen flex-auto overflow-hidden rounded-lg bg-shades-baseLighter backdrop-blur-md text-sm leading-6 shadow-lg ring-1 ring-gray-900/5 max-h-[calc(100vh-200px)] relative right-[1px] py-8 justify-center items-center">
      <ExclamationTriangleIcon className="w-12 h-12 text-colors-red" />
      <p className="mt-4 text-sm font-semibold leading-6 text-colors-red">
        Error loading maps. Please try again.
      </p>
    </div>
  );
};

export default RoadMapPanelHeader;
